import {
	Box,
	IconButton,
	Tooltip,
	Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ASSETS_BASE_URL, SERVER_BASE_URL } from "../../config";
import { useSite } from "../../context/siteContext";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/cartContext";
import { useAuth } from "../../context/authContext";
import { BUTTON, LABELS, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { saveRequestList } from "../../services/productCardService";
import { toast } from "../../context/ToastContext";

import formatCurrency from "../../utilities/currency";
import { AddShoppingCart } from "@mui/icons-material";
import useBrandingAssets from "../../hooks/useBrandingAssets";
import ReplacementPartNotice from "../ReplacementPartNotice/ReplacementPartNotice";
import { getMinimumSalesOrderQuantity, getMultipleSalesOrderQuantity, normalizeOrderQuantity } from "../../utilities/orderQuantity";
import { getEffectiveProductTarget } from "../../utilities/replacementProduct";
const TopProductListItem = ({
	product,
	machine_id,
	source = "products_page",
}) => {
	const navigate = useNavigate();
	const { selectedSite, selectedSiteCurrency } = useSite();
	const [isLoading, setIsLoading] = useState(false); // To manage the loading state
	const [isSuccess, setIsSuccess] = useState(false);
	const { addToCart } = useCart();
	const { user, canViewProductPrice, canAddToCart, canAddToRequestList } = useAuth();
    const { stockImageUrl } = useBrandingAssets();
    const [imageSrc, setImageSrc] = useState(stockImageUrl || `${ASSETS_BASE_URL}/stock.png`);
	const [price, setPrice] = useState(0);
	const { t } = useTranslation();
	const canShowPrice = canViewProductPrice();
	const canShowAddToCart = canAddToCart();
	const canShowAddToRequestList = canAddToRequestList();
	const triggerToast = (message, severity = "success") => {
		if (!message) return;
		toast.trigger({ message, severity });
	};

	useEffect(() => {
		const raw = product?.asset_data?.images?.[0];
		if (raw) {
			if (/^(https?:)?\/\//.test(raw) || raw.startsWith("data:")) {
				setImageSrc(raw);
				return;
			}
			const path = raw.startsWith("/") ? raw : `/${raw}`;
			setImageSrc(`${ASSETS_BASE_URL}${path}`);
			return;
		}
		if (product?.product_number) {
			setImageSrc(`${ASSETS_BASE_URL}/ProductImages/TMB/${product.product_number}.JPG`);
			return;
		}
		setImageSrc(stockImageUrl || `${ASSETS_BASE_URL}/stock.png`);
		// eslint-disable-next-line
	}, [product.product_number, product?.asset_data, stockImageUrl]);

	useEffect(() => {
		if (product && selectedSiteCurrency) {
			const key = `price_${selectedSiteCurrency}`;
			const currencyPrice = product[key];
			const finalPrice = Number(product.purchasable) === 1 ? currencyPrice : 0;
			
			setPrice(finalPrice);
		}
	}, [product, selectedSiteCurrency]);

	const handleProductPageNaviagte = (product_id) => {
		navigate(`/machines/${machine_id}/${product_id}`);
	};
	const actionTarget = getEffectiveProductTarget(product, {
		machineId: machine_id || product?.machine_id,
		currency: selectedSiteCurrency,
	});
	const quantityProduct = actionTarget?.replaced ? actionTarget : product;
	const isPurchasable = actionTarget?.replaced
		? actionTarget.purchasable
		: Number(product.purchasable) === 1;
	const displayPrice = actionTarget?.replaced ? actionTarget.price : price;
	const displayLineDiscountApplied = actionTarget?.replaced
		? actionTarget.line_discount_applied
		: product.line_discount_applied;
	const handleAddToCart = async (event) => {
		event.stopPropagation();
		if (!canShowAddToCart) return;

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);
		try {
			const minimumSalesOrderQuantity = getMinimumSalesOrderQuantity(quantityProduct);
			const multipleSalesOrderQuantity = getMultipleSalesOrderQuantity(quantityProduct);
			// Call the addToCart function (which you've defined to add the product)
			await addToCart({
				...product,
				product_id: actionTarget?.product_id || product.product_id,
				ProductID: actionTarget?.product_id || product.product_id,
				machine_id: actionTarget?.machine_id || machine_id || product.machine_id,
				MachineID: actionTarget?.machine_id || machine_id || product.machine_id,
				Quantity: normalizeOrderQuantity(1, quantityProduct),
				Site: selectedSite,
				minimum_sales_order_quantity: minimumSalesOrderQuantity,
				multiple_sales_order_quantity: multipleSalesOrderQuantity,
			});

			// Set success state
			setIsSuccess(true);
			triggerToast(
				t("ALERT_MESSAGES.PRODUCT_ADDED_TO_CART", { productNumber: product.product_number }),
				"success"
			);
			// Reset the button text back to "Add to Cart" after 2 seconds
			setTimeout(() => {
				setIsLoading(false);
				setIsSuccess(false);
			}, 2000); // 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			triggerToast(`Error adding ${product.product_name} to cart`, "error");
		} finally {
			// Stop the loading spinner after a delay and reset the button state
			setIsLoading(false);
		}
	};

	const handleAddToRequestList = async (event) => {
		event.stopPropagation();
		if (!canShowAddToRequestList) return;

		// Start the loading state and reset success state
		setIsLoading(true);
		setIsSuccess(false);
		try {
			// Call the addToCart function (which you've defined to add the product)
			// Set success state
			const req_body = {
				machine_id: actionTarget?.machine_id || machine_id,
				product_id: actionTarget?.product_id || product.product_id,
				site_number: selectedSite,
				user_id: user.user_id,
			};

			try {
				const response = await saveRequestList(req_body);
				if (response.status) {
					setIsSuccess(true);
					setIsLoading(false);
					triggerToast(
						t("ALERT_MESSAGES.PRODUCT_ADDED_TO_REQUEST", {
							productName: product.product_name,
						}),
						"success"
					);

					setTimeout(() => {
						setIsLoading(false);
						setIsSuccess(false);
					}, 1000);
				} else if (!response.status) {
					triggerToast(
						t("ALERT_MESSAGES.PRODUCT_ALREADY_IN_REQUEST", {
							productName: product.product_name,
						}),
						"warning"
					);
				}
			} catch (error) {
				console.error("Error fetching product name:", error);
			}

			// Reset the button text back to "Add to Cart" after 2 seconds
			// 2 seconds delay
		} catch (error) {
			// Handle any error (optional)
			setIsSuccess(false);
			triggerToast(`Error adding ${product.product_name} to request list`, "error");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<>
			<Box
				sx={{
					width: "100%",
					maxHeight: "75px",
					borderRadius: 2,
					display: "flex",
					flexDirection: "row",
					justifyContent: "flex-start",
					alignItems: "center",
					backgroundColor: "#fff",
					boxShadow: "0 0 6px 1px rgba(0,0,0,0.1)",
					p: 2,
					columnGap: 2,	
					margin: "7px 0",
					"&:hover": {
						boxShadow: "0 0 10px 2px rgba(0,0,0,0.2)",
					},
				}}
				onClick={() => {
					handleProductPageNaviagte(product.product_id);
				}}
			>
                <img
                    style={{ height: "50px", width: "50px", flexShrink: 0 }}
                    src={imageSrc}
                    alt={product.product_name}
                    onError={(e) => { e.currentTarget.src = stockImageUrl || `${ASSETS_BASE_URL}/stock.png`; }}
                />
				{/* minWidth:0 + width:100% on the lines lets the name and part/machine line
				    run all the way to the action icons before they ellipse, instead of
				    stopping at a fixed maxWidth well short of them. */}
				<Box sx={{ flex: 1, minWidth: 0, cursor: "pointer" }}>
					<Tooltip title={product.product_name}>
						<Typography
							variant="body1"
							sx={{
								fontWeight: "bold",
								fontSize: "12px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								width: "100%",
							}}
						>
							{product.product_name}
						</Typography>
					</Tooltip>

					<Tooltip
						title={
							source === "search_results"
								? `${t("UI_TEXT.PART")} ${product.product_number || ""} | ${t("UI_TEXT.MACHINE")} ${product.machine_number || ""}`
								: `${t("UI_TEXT.PART")} ${product.product_number || ""}`
						}
					>
						<Typography
							variant="body1"
							color="text.secondary"
							sx={{
								fontSize: "11px",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
								width: "100%",
							}}
						>
							{source === "search_results" ? (
								<>
									<b>{t("UI_TEXT.PART")} </b> {product.product_number} {" | "}
									<b>{t("UI_TEXT.MACHINE")} </b> {product.machine_number}
								</>
							) : (
								<>
										<b>{t("UI_TEXT.PART")} </b> {product.product_number}
								</>
							)}
						</Typography>
					</Tooltip>
					<ReplacementPartNotice product={product} />
					{source === "products_page" && canShowPrice && (
						<Typography
							variant="body1"
							color="primary"
							sx={{ fontSize: "14px" }}
						>
							<b>
								{!isPurchasable
									? ""
									: (displayPrice === "0.00" || displayPrice === 0) && !displayLineDiscountApplied
										? ""
										: formatCurrency(
											displayPrice,
											selectedSiteCurrency
									)}
							</b>
						</Typography>
					)}
				</Box>

				<Box
					display={"flex"}
					justifyContent={"flex-end"}
					alignItems={"center"}
					columnGap={2}
					sx={{ flexShrink: 0 }}
				>
					{source !== "products_page" && canShowPrice && (
						<Typography
							variant="body1"
							color="primary"
							sx={{ fontSize: "14px" }}
						>
							<b>
								{!isPurchasable
									? ""
									: (displayPrice === "0.00" || displayPrice === 0) && !displayLineDiscountApplied
										? ""
										: formatCurrency(
											displayPrice,
											selectedSiteCurrency
									)}
							</b>
						</Typography>
					)}
					{!isPurchasable && canShowAddToRequestList ? (
						<Tooltip title={t("BUTTON.ADD_TO_REQUEST_LIST")}>
							<IconButton
								color="primary"
								onClick={handleAddToRequestList}
							>
								{/* Different icon or style here */}
								<SummarizeIcon />{" "}
								{/* example: a blocked icon */}
							</IconButton>
						</Tooltip>
					) : product.purchasable !== 0 && canShowAddToCart ? (
							<Tooltip title={t("LABELS.ADD_TO_CART")}>
								<IconButton
									color="primary"
									onClick={handleAddToCart}
								>
									<AddShoppingCart />
							</IconButton>
						</Tooltip>
					) : null}
				</Box>
			</Box>
		</>
	);
};

export default TopProductListItem;
